import TextForm from "./TextForm.tsx";
import {Button, Card, CardMedia, Checkbox, FormControlLabel, Grid2, List, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {FormEvent, useContext, useEffect, useState} from "react";
import ProductContext from "../contexts/ProductContext.tsx";
import SaleContext from "../contexts/SaleContext.tsx";
import {ISale} from "../models/ISale.ts";
import {IProduct} from "../models/IProduct.ts";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

interface IProps {
    isCreate: boolean
}

const SaleForm = ({isCreate}: IProps) => {
    const {t} = useTranslation();
    const {products} = useContext(ProductContext);
    const {createSaleMutation, updateSaleMutation, sale} = useContext(SaleContext);

    const [updatedSale, setUpdatedSale] = useState<ISale>({
        from: new Date(),
        to: new Date(),
        discount: 0,
        items: [] as IProduct[]
    });
    const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([]);
    const [selectAll, setSelectAll] = useState(false);

  

    useEffect(() => {
        if (!isCreate && sale) {
            setUpdatedSale({
                ...sale,
                from: new Date(sale.from),
                to: new Date(sale.to),
            });
            setSelectedProducts(sale.items as IProduct[]);
        }
    }, [sale, isCreate]);

    const formatDate = (date: Date) => {
        return date.toISOString().split('T')[0];
    };

    const formLabels = [
        `${t('SALE_FROM_TEXT')}`,
        `${t('SALE_TO_TEXT')}`,
        `${t('SALE_DISCOUNT_TEXT')}`
    ];

    const formData = {
        from: {
            value: formatDate(updatedSale.from),
            required: true,
            date: true
        },
        to: {
            value: formatDate(updatedSale.to),
            required: true,
            date: true
        },
        discount: {value: updatedSale.discount * 100, required: true, percentage: true},
    };

    const handleChange = (key: string, value: any) => {
        setUpdatedSale((prevSale) => ({
            ...prevSale,
            [key]: (key === "from" || key === "to") ? new Date(value) : value/100
        }));
    };

    const handleProductToggle = (product: IProduct) => {
        setSelectedProducts(prevSelected => {
            let newSelected: IProduct[];
            if (prevSelected.find(p => p._id === product._id)) {
                newSelected = prevSelected.filter(p => p._id !== product._id);
            } else {
                newSelected = [...prevSelected, product];
            }
            setUpdatedSale(prevSale => ({
                ...prevSale,
                items: newSelected
            }));
            return newSelected;
        });
    };

    const handleSelectAllToggle = () => {
        if (selectAll) {
            setSelectedProducts([]);
            setUpdatedSale(prevSale => ({
                ...prevSale,
                items: []
            }));
        } else {
            setSelectedProducts(products);
            setUpdatedSale(prevSale => ({
                ...prevSale,
                items: products
            }));
        }
        setSelectAll(!selectAll);
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const saleToSubmit = {
            ...updatedSale,
            from: updatedSale.from.toISOString(),
            to: updatedSale.to.toISOString(),
        };

        if (isCreate) await createSaleMutation.mutateAsync(saleToSubmit)
        else await updateSaleMutation.mutateAsync(saleToSubmit)
    };

    const isProductSelected = (productId: string | undefined) => {
        return selectedProducts.some(p => p._id === productId);
    };

    return (
        <form onSubmit={handleSubmit}>
            <TextForm
                formLabels={formLabels}
                formData={formData}
                formHeader={t("CREATE_SALE")}
                onChange={handleChange}
            />
            <Typography paddingLeft={2} paddingTop={2}>
                {t("SALE_PRODUCTS_TEXT")}
            </Typography>
            <FormControlLabel
                sx={{pl: 2}}
                control={<Checkbox checked={selectAll} onChange={handleSelectAllToggle}/>}
                label={
                    <Typography variant={"body2"}>
                        {t("ADD_ALL_TO_SALE")}
                    </Typography>
                }
            />
            <List>
                {products && products.length > 0 &&
                    products.map((product) => (
                        <Card sx={{padding: 1, margin: 2}} key={product._id}>
                            <Grid2 container>
                                <Grid2 size={7}>
                                    <Typography variant={"body2"}>{product.name.en}</Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isProductSelected(product._id)}
                                                onChange={() => handleProductToggle(product)}
                                            />
                                        }
                                        label={
                                            <Typography variant={"body2"}>
                                                {t("INCLUDE_IN_SALE")}
                                            </Typography>
                                        }
                                    />
                                </Grid2>
                                <Grid2 size={5}>
                                    <CardMedia
                                        component="img"
                                        loading="lazy"
                                        image={`${VITE_API_BASE_URL}/${product.images[0]}`}
                                        sx={{borderRadius: "8px", maxWidth: "60%"}}
                                    />
                                </Grid2>
                            </Grid2>
                        </Card>
                    ))}
            </List>
            <Button
                sx={{
                    ml: 3,
                    mt: 2
                }}
                type="submit"
                variant="contained"
                color="primary"
            >
                {isCreate ? t('START_SALE') : t('UPDATE_SALE')}
            </Button>
        </form>
    )
}

export default SaleForm;